import React from "react";
import { TableRow, TableCell, Box, Typography, Chip, Button, Select, MenuItem } from "@mui/material";
import { styled } from "@mui/material/styles";
import { getStatusInfo, getPaymentStatusChipProps } from "../constants/orderConstants";

/**
 * Format date string to locale date
 */
const formatDate = (dateString) => {
  if (!dateString) return "---";
  return new Date(dateString).toLocaleDateString();
};

/**
 * Styled TableRow with custom border and hover styles
 */
const StyledTableRow = styled(TableRow)(() => ({
  cursor: "pointer",
  borderRadius: "10px",
  background: "white",
  marginBottom: "10px",
  "& td": {
    borderTop: "1px solid rgba(224, 224, 229, 1)",
    borderBottom: "1px solid rgba(224, 224, 229, 1)",
    borderRight: "none",
  },
  "& td:first-of-type": {
    borderTopLeftRadius: "10px",
    borderBottomLeftRadius: "10px",
    borderLeft: "1px solid rgba(224, 224, 229, 1)",
    borderRight: "none",
  },
  "& td:last-child": {
    borderTopRightRadius: "10px",
    borderBottomRightRadius: "10px",
    borderRight: "1px solid rgba(224, 224, 229, 1)",
    borderLeft: "none",
  },
}));

/**
 * Status badge component
 */
const StatusBadge = ({ status }) => {
  const statusInfo = getStatusInfo(status);

  if (!statusInfo) return null;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        border: "1px solid",
        borderColor: statusInfo.color,
        borderRadius: "10px",
        padding: "6px 12px",
        width: "fit-content",
      }}
    >
      {statusInfo.icon && (
        <img src={statusInfo.icon} alt="" style={{ color: statusInfo.color }} />
      )}
      <Typography
        sx={{ fontSize: "0.875rem", color: statusInfo.color, ml: 1 }}
      >
        {statusInfo.label}
      </Typography>
    </Box>
  );
};

/**
 * Table row component for displaying individual order
 */
const OrderTableRow = ({ order, onViewOrder, onExportCSV, onManageAccount, onCancelledChange }) => {
  const paymentStatusProps = getPaymentStatusChipProps(order?.paymentStatus ? order?.paymentStatus : 'active');

  return (
    <StyledTableRow>
      <TableCell align="left" sx={{ borderBottom: "none", width: "fit-content" }}>
        <Box sx={{ display: "flex", alignItems: "center", fontSize: "14px" }}>{order.userEmail}</Box>
      </TableCell>

      <TableCell align="left" sx={{ borderBottom: "none", width: "fit-content" }}>
        <Box sx={{ display: "flex", alignItems: "center", fontSize: "14px" }}>{formatDate(order.createdAt)}</Box>
      </TableCell>

      <TableCell align="left" sx={{ borderBottom: "none" }}>
        <StatusBadge status={order.status} />
      </TableCell>

      <TableCell align="left" sx={{ borderBottom: "none", width: "fit-content" }}>
        <Box sx={{ display: "flex", alignItems: "center", fontSize: "14px" }}>
          <Chip size="small" variant="filled" {...paymentStatusProps} />
        </Box>
      </TableCell>

      <TableCell
        align="left"
        sx={{
          display: "flex",
          borderBottom: "none",
          width: "fit-content",
          padding: "10px",
        }}
      >
        <Button onClick={() => onViewOrder(order)}>View Order</Button>
        <Button
          variant="contained"
          color="success"
          sx={{ margin: "0px 10px" }}
          onClick={() => onExportCSV(order)}
        >
          Export CSV
        </Button>
        <Button
          variant="contained"
          color="info"
          sx={{ margin: "0px 20px" }}
          onClick={() => onManageAccount(order.user)}
        >
          Manage Account
        </Button>
      </TableCell>

      <TableCell align="left" sx={{ borderBottom: "none", width: "fit-content" }}>
        <Select
          size="small"
          value={order.cancelledFromDFY ?? "NA"}
          onChange={(e) => onCancelledChange(order._id, e.target.value)}
          sx={{
            minWidth: 90,
            fontSize: "14px",
            borderRadius: "8px",
            backgroundColor: "#fff",
          }}
        >
          <MenuItem value="N/A" disabled={order.cancelledFromDFY && order.cancelledFromDFY !== "N/A" && order.cancelledFromDFY !== "NA"}>N/A</MenuItem>
          <MenuItem value="Yes">Yes</MenuItem>
          <MenuItem value="No">No</MenuItem>
        </Select>
      </TableCell>
    </StyledTableRow>
  );
};

export default OrderTableRow;